/**
 * Gesture Animation System
 * Implements natural gestures (nod, shake, shrug, lean) with keyword triggers
 * Used to make the avatar more expressive during conversations
 */

import nodData from '../data/blendDataGestureNod.json';
import shakeData from '../data/blendDataGestureShake.json';
import shrugData from '../data/blendDataGestureShrug.json';

export type GestureType = 'nod' | 'shake' | 'shrug' | 'lean-forward' | 'lean-back' | 'wave';

export interface GestureFrame {
  blendshapes: Record<string, number>;
  headPosition?: { x: number; y: number; z: number };
  headRotation?: { x: number; y: number; z: number };
  time: number;
}

export interface GestureConfig {
  type: GestureType;
  intensity?: number;  // 0-1, default 1.0
  duration?: number;   // Override default duration
}

// Keyword patterns that trigger gestures
export const GESTURE_TRIGGERS: Record<GestureType, string[]> = {
  'wave': [
    'hello', 'hi ', 'hey ', 'welcome', 'goodbye', 'bye', 'wave',
    '\u{1F44B}'
  ],
  'nod': [
    'yes', 'yeah', 'yep', 'correct', 'right', 'exactly', 'absolutely',
    'sure', 'definitely', 'of course', 'indeed', 'agreed', 'okay',
    'understand', 'got it', 'i see', 'makes sense'
  ],
  'shake': [
    'no', 'nope', 'not', 'never', 'wrong', 'incorrect', 'negative',
    'unfortunately', 'cannot', 'unable', 'impossible', 'denied',
    'reject', 'refuse', 'disagree'
  ],
  'shrug': [
    'maybe', 'perhaps', 'not sure', 'uncertain', 'possibly', 'might',
    'could be', 'hard to say', 'difficult to', 'depends', 'unsure',
    "don't know", 'unclear', 'questionable', 'either way', 'hmm'
  ],
  'lean-forward': [
    'important', 'listen', 'attention', 'focus', 'critical', 'key point',
    'essential', 'crucial', 'significant', 'note that', 'remember',
    'interesting', 'exciting', 'great news'
  ],
  'lean-back': [
    'think about', 'consider', 'reflect', 'ponder', 'let me see',
    'well', 'so', 'hmm', 'actually', 'in fact', 'to be honest'
  ]
};

// Generate lean forward animation data
function generateLeanForwardData(): GestureFrame[] {
  return [
    {
      blendshapes: {
        eyeWideLeft: 0.1,
        eyeWideRight: 0.1,
        browInnerUp: 0.1
      },
      headPosition: { x: 0, y: 0, z: 0 },
      headRotation: { x: 0, y: 0, z: 0 },
      time: 0
    },
    {
      blendshapes: {
        eyeWideLeft: 0.2,
        eyeWideRight: 0.2,
        browInnerUp: 0.15,
        browOuterUpLeft: 0.1,
        browOuterUpRight: 0.1
      },
      headPosition: { x: 0, y: -0.01, z: 0.03 },
      headRotation: { x: 0.05, y: 0, z: 0 },
      time: 0.3
    },
    {
      blendshapes: {
        eyeWideLeft: 0.25,
        eyeWideRight: 0.25,
        browInnerUp: 0.2,
        browOuterUpLeft: 0.15,
        browOuterUpRight: 0.15,
        mouthSmileLeft: 0.05,
        mouthSmileRight: 0.05
      },
      headPosition: { x: 0, y: -0.015, z: 0.05 },
      headRotation: { x: 0.08, y: 0, z: 0 },
      time: 0.5
    },
    {
      blendshapes: {
        eyeWideLeft: 0.15,
        eyeWideRight: 0.15,
        browInnerUp: 0.12
      },
      headPosition: { x: 0, y: -0.008, z: 0.025 },
      headRotation: { x: 0.04, y: 0, z: 0 },
      time: 0.8
    },
    {
      blendshapes: {
        eyeWideLeft: 0.08,
        eyeWideRight: 0.08,
        browInnerUp: 0.08
      },
      headPosition: { x: 0, y: 0, z: 0 },
      headRotation: { x: 0, y: 0, z: 0 },
      time: 1.0
    }
  ];
}

// Generate lean back animation data
function generateLeanBackData(): GestureFrame[] {
  return [
    {
      blendshapes: {
        eyeSquintLeft: 0.05,
        eyeSquintRight: 0.05
      },
      headPosition: { x: 0, y: 0, z: 0 },
      headRotation: { x: 0, y: 0, z: 0 },
      time: 0
    },
    {
      blendshapes: {
        eyeSquintLeft: 0.15,
        eyeSquintRight: 0.15,
        browDownLeft: 0.1,
        browDownRight: 0.1,
        mouthPucker: 0.05
      },
      headPosition: { x: 0.005, y: 0.01, z: -0.02 },
      headRotation: { x: -0.03, y: 0.02, z: 0 },
      time: 0.3
    },
    {
      blendshapes: {
        eyeSquintLeft: 0.2,
        eyeSquintRight: 0.2,
        browDownLeft: 0.15,
        browDownRight: 0.15,
        mouthPucker: 0.08,
        browInnerUp: 0.1
      },
      headPosition: { x: 0.008, y: 0.015, z: -0.035 },
      headRotation: { x: -0.05, y: 0.03, z: 0.01 },
      time: 0.5
    },
    {
      blendshapes: {
        eyeSquintLeft: 0.12,
        eyeSquintRight: 0.12,
        browDownLeft: 0.08,
        browDownRight: 0.08
      },
      headPosition: { x: 0.004, y: 0.008, z: -0.018 },
      headRotation: { x: -0.025, y: 0.015, z: 0.005 },
      time: 0.8
    },
    {
      blendshapes: {
        eyeSquintLeft: 0.05,
        eyeSquintRight: 0.05
      },
      headPosition: { x: 0, y: 0, z: 0 },
      headRotation: { x: 0, y: 0, z: 0 },
      time: 1.0
    }
  ];
}

// Wave gesture face component (friendly expression while waving).
//
// IMPORTANT: keep `headRotation` at zero. Pre-v2.7.x this function applied
// a side-to-side head wobble (`y: 0.05` then `y: -0.04`) "for friendliness",
// but combined with a 1024px-tight portrait crop the head wobble visually
// dominated the arm motion — operators reported the wave "looks like the
// avatar is nodding their head back and forth". The arm-bone keyframes
// in GESTURE_BONE_KEYFRAMES.wave are the wave; the face only adds a
// smile + eye-widen.
function generateWaveFaceData(): GestureFrame[] {
  return [
    {
      blendshapes: {},
      headRotation: { x: 0, y: 0, z: 0 },
      time: 0
    },
    {
      blendshapes: {
        mouthSmileLeft: 0.3,
        mouthSmileRight: 0.3,
        browOuterUpLeft: 0.1,
        browOuterUpRight: 0.1,
        eyeWideLeft: 0.1,
        eyeWideRight: 0.1
      },
      headRotation: { x: 0, y: 0, z: 0 },
      time: 0.3
    },
    {
      blendshapes: {
        mouthSmileLeft: 0.35,
        mouthSmileRight: 0.35,
        browOuterUpLeft: 0.15,
        browOuterUpRight: 0.15,
        eyeWideLeft: 0.12,
        eyeWideRight: 0.12,
        cheekSquintLeft: 0.1,
        cheekSquintRight: 0.1
      },
      headRotation: { x: 0, y: 0, z: 0 },
      time: 0.8
    },
    {
      blendshapes: {
        mouthSmileLeft: 0.3,
        mouthSmileRight: 0.3,
        browOuterUpLeft: 0.1,
        browOuterUpRight: 0.1,
      },
      headRotation: { x: 0, y: 0, z: 0 },
      time: 1.5
    },
    {
      blendshapes: {
        mouthSmileLeft: 0.15,
        mouthSmileRight: 0.15,
      },
      headRotation: { x: 0, y: 0, z: 0 },
      time: 2.2
    },
    {
      blendshapes: {},
      headRotation: { x: 0, y: 0, z: 0 },
      time: 2.8
    }
  ];
}

/**
 * Bone roles used by the procedural skeletal animation system.
 * Each role maps to a list of candidate bone names — the renderer picks
 * the first one that exists on the loaded model. This keeps gestures
 * working across rigs that use different conventions:
 *   • CamelCase (some glb exports preserve raw bone names)
 *   • lowercase Mixamo (head, neck, spine2, rightShoulder, …)
 *   • mixamorig: prefixed (FBX-from-Mixamo)
 *   • UE5 MetaHuman / X3D (clavicle_r, upperarm_r, spine_03, …)
 *   • Blender Rigify (shoulder.R, upper_arm.R, forearm.R, spine.001, …)
 *
 * NOTE on Rigify "dot" names: GLTFLoader / Three.js' PropertyBinding
 * strips reserved characters (`.[]:/`) from node names at load time
 * because they conflict with PropertyBinding's path syntax. So a
 * Rigify rig authored as `shoulder.R` / `spine.001` shows up at runtime
 * as `shoulderR` / `spine001`. We list BOTH the original-with-dot AND
 * the sanitised-no-dot forms so a future GLTFLoader version that stops
 * sanitising (or a different loader) still matches. The case-
 * insensitive fallback in `resolveBoneNameForRole` catches everything
 * else (e.g. `Shoulder.r`, `SHOULDER.L`).
 */
export type BoneRole =
  | 'rightShoulder' | 'rightUpperArm' | 'rightForeArm' | 'rightHand'
  | 'leftShoulder'  | 'leftUpperArm'  | 'leftForeArm'  | 'leftHand'
  | 'spine'         | 'chest'
  | 'neck'          | 'head';

export const BONE_CANDIDATES: Record<BoneRole, string[]> = {
  rightShoulder: [
    'RightShoulder', 'rightShoulder', 'mixamorig:RightShoulder',
    'right_clavicle_01', 'clavicle_r', 'clavicle_R',
    // Rigify
    'shoulder.R', 'shoulderR',
  ],
  rightUpperArm: [
    'RightArm', 'rightArm', 'mixamorig:RightArm',
    'right_arm_02', 'upperarm_r', 'upperarm_R',
    'upper_arm.R', 'upper_armR',
  ],
  rightForeArm: [
    'RightForeArm', 'rightForeArm', 'mixamorig:RightForeArm',
    'right_arm_03', 'lowerarm_r', 'lowerarm_R',
    'forearm.R', 'forearmR',
  ],
  rightHand: [
    'RightHand', 'rightHand', 'mixamorig:RightHand',
    'hand_r', 'hand_R',
    'hand.R', 'handR',
  ],
  leftShoulder: [
    'LeftShoulder', 'leftShoulder', 'mixamorig:LeftShoulder',
    'left_clavicle_01', 'clavicle_l', 'clavicle_L',
    'shoulder.L', 'shoulderL',
  ],
  leftUpperArm: [
    'LeftArm', 'leftArm', 'mixamorig:LeftArm',
    'left_arm_02', 'upperarm_l', 'upperarm_L',
    'upper_arm.L', 'upper_armL',
  ],
  leftForeArm: [
    'LeftForeArm', 'leftForeArm', 'mixamorig:LeftForeArm',
    'left_arm_03', 'lowerarm_l', 'lowerarm_L',
    'forearm.L', 'forearmL',
  ],
  leftHand: [
    'LeftHand', 'leftHand', 'mixamorig:LeftHand',
    'hand_l', 'hand_L',
    'hand.L', 'handL',
  ],
  // "spine" = lower back (lean from the hips); "chest" = upper torso.
  // For Rigify (4-bone spine: spine, spine001, spine002, spine003),
  // we map `spine` → spine.000 (lumbar) and `chest` → spine.002/003 (upper).
  // For UE5 X3D (5-bone spine: spine_01..spine_05), `spine` → spine_02
  // (mid-lumbar) and `chest` → spine_05/04/03 (upper torso).
  spine: [
    'Spine', 'spine', 'mixamorig:Spine',
    'spine_02', 'spine_01',
    // Rigify (sanitised name lookup also caught by case-fold fallback)
    'spine.001', 'spine001',
  ],
  chest: [
    'Spine2', 'spine2', 'mixamorig:Spine2',
    'Spine1', 'spine1', 'mixamorig:Spine1',
    'spine_05', 'spine_04', 'spine_03',
    // Rigify upper torso
    'spine.003', 'spine003', 'spine.002', 'spine002',
  ],
  // Head/neck. Mixamo, Rigify, MetaHuman all put a discrete `head`
  // bone on top of `neck`. Some X3D rigs (m4-joel.glb / Joel,
  // confirmed Apr-30 2026 in the Animation Tester) skip the head
  // bone entirely — the chain is `... spine_05 → neck_01 → neck_02`
  // with the head mesh skinned to `neck_02`. For those rigs we
  // fall back to using the topmost neck bone (`neck_02` / `neck_03`)
  // as the head — rotating it produces the same visual nod/shake.
  // The `neck` role still picks `neck_01` so the nod blends from
  // the lower neck up through `neck_02`/head; in rigs that DO have
  // a separate `head`, the resolver hits `Head` first and the neck
  // candidate falls through to `neck_01`. Order in this candidate
  // list is significance: most-canonical first, X3D-fallback last.
  neck: [
    'Neck', 'neck', 'mixamorig:Neck',
    'neck_01',
    // Rigify
    'neck.001', 'neck001',
    // X3D fallback (only used if `neck_01` doesn't exist on the rig)
    'neck_02',
  ],
  head: [
    'Head', 'head', 'mixamorig:Head',
    'head_01', 'head_02',
    // Rigify
    'head.001', 'head001',
    // X3D rigs without a discrete head bone (Joel) — last-resort
    // fallback walks the neck chain top-down. `neck_03` first so
    // 4-bone X3D necks (uncommon) don't pick the wrong end.
    'neck_03', 'neck_02',
  ],
};

export interface SkeletalGestureKeyframes {
  times: number[];
  /** role → array of Euler frames (radians); length must match `times`. */
  bones: Partial<Record<BoneRole, { x: number; y: number; z: number }[]>>;
  /**
   * When true, the renderer interprets each frame's `{x, y, z}` as a
   * rotation around the WORLD x/y/z axes (then converts to bone-local
   * via the parent's world-quaternion inverse). This makes the gesture
   * direction-stable across rigs whose spine bones were authored with
   * different local axis conventions — e.g. Sam (Mixamo) leans forward
   * cleanly when rotating around bone-local-X, but Cara / Jason use a
   * spine rig whose local-X axis points sideways in WORLD space, so
   * the same Euler tilts the body left/right instead.
   *
   * Default: false (bone-local Euler — matches the v2.7.x behaviour
   * for arm gestures like wave/shrug, which DO want bone-local axes
   * because shoulder bones share an outward-pointing local-Z across
   * rigs by convention).
   */
  worldSpaceAxes?: boolean;
}

/**
 * Procedural bone keyframes for gestures that animate the body, not just
 * the face. Frames are ADDITIVE — applied on top of whatever idle/voice
 * animation is currently playing. Euler angles are in radians.
 */
export const GESTURE_BONE_KEYFRAMES: Partial<Record<GestureType, SkeletalGestureKeyframes>> = {
  // Right-arm wave with hand at HEAD HEIGHT and palm facing FORWARD,
  // not the half-mast across-the-chest motion v2.7.x produced.
  //
  // Key tuning notes:
  //   • rightUpperArm.z = -1.55 raises the arm to ~89° from rest
  //     (rest = arm hanging down, -1.57 = arm horizontal, anything
  //     past that points up; we land just shy of horizontal so the
  //     elbow comes up to roughly shoulder/jaw height — not above
  //     the head, which looks like a referee's signal).
  //   • rightUpperArm.x = -0.25 rotates the arm slightly forward
  //     so the elbow sits in front of the shoulder rather than
  //     directly to the side. Without this, the wave looks like a
  //     "stop" pose with the arm out at 3 o'clock.
  //   • rightForeArm.y = -1.4 bends the elbow ~80° so the hand
  //     reaches the head/face area. Combined with the upper-arm
  //     lift the wrist sits at roughly cheek height — that's the
  //     visual goal.
  //   • rightHand.z = -0.5 rotates the wrist so the palm faces
  //     forward (toward the camera) instead of edge-on, then the
  //     `x` waggle on top of that rocks the open palm side-to-side.
  //   • The first/last frames blend in/out from the rest pose so
  //     the gesture eases instead of snapping. Total duration
  //     2.6s matches the previous timing so downstream consumers
  //     (TTS/ASR) don't see a behavioural change.
  wave: {
    times: [0, 0.4, 0.7, 1.0, 1.3, 1.6, 1.9, 2.2, 2.6],
    bones: {
      rightShoulder: [
        { x: 0, y: 0, z: 0 },
        { x: 0, y: 0, z: -0.30 },
        { x: 0, y: 0, z: -0.30 },
        { x: 0, y: 0, z: -0.30 },
        { x: 0, y: 0, z: -0.30 },
        { x: 0, y: 0, z: -0.30 },
        { x: 0, y: 0, z: -0.30 },
        { x: 0, y: 0, z: -0.15 },
        { x: 0, y: 0, z: 0 },
      ],
      rightUpperArm: [
        { x: 0,     y: 0, z: 0     },
        { x: -0.25, y: 0, z: -1.55 },
        { x: -0.25, y: 0, z: -1.55 },
        { x: -0.25, y: 0, z: -1.55 },
        { x: -0.25, y: 0, z: -1.55 },
        { x: -0.25, y: 0, z: -1.55 },
        { x: -0.25, y: 0, z: -1.55 },
        { x: -0.12, y: 0, z: -0.75 },
        { x: 0,     y: 0, z: 0     },
      ],
      rightForeArm: [
        { x: 0, y: 0,    z: 0 },
        { x: 0, y: -1.40, z: 0 },
        { x: 0, y: -1.40, z: 0 },
        { x: 0, y: -1.40, z: 0 },
        { x: 0, y: -1.40, z: 0 },
        { x: 0, y: -1.40, z: 0 },
        { x: 0, y: -1.40, z: 0 },
        { x: 0, y: -0.70, z: 0 },
        { x: 0, y: 0,    z: 0 },
      ],
      rightHand: [
        { x: 0,    y: 0, z: 0    },
        { x: 0,    y: 0, z: -0.5 },
        { x: 0.4,  y: 0, z: -0.5 },
        { x: -0.4, y: 0, z: -0.5 },
        { x: 0.4,  y: 0, z: -0.5 },
        { x: -0.4, y: 0, z: -0.5 },
        { x: 0.3,  y: 0, z: -0.5 },
        { x: 0,    y: 0, z: -0.25 },
        { x: 0,    y: 0, z: 0    },
      ],
    },
  },

  // Nod (yes): chin DOWN twice, then return to rest.
  // worldSpaceAxes so rotating around world-X (left-right horizon)
  // gives the same forward-tilt motion regardless of rig
  // convention. Without this, rigs whose head local-X points
  // sideways (Cara, Jason, several Xandra rigs) tilt the head
  // LEFT-RIGHT instead of forward/back.
  nod: {
    worldSpaceAxes: true,
    times: [0, 0.28, 0.55, 0.82, 1.10, 1.35],
    bones: {
      head: [
        { x: 0,    y: 0, z: 0 },
        { x: 0.11, y: 0, z: 0 },
        { x: 0.02, y: 0, z: 0 },
        { x: 0.09, y: 0, z: 0 },
        { x: 0.02, y: 0, z: 0 },
        { x: 0,    y: 0, z: 0 },
      ],
      // Tiny neck contribution (10% of head) so the nod looks like
      // it's coming from the upper neck, not just the skull.
      neck: [
        { x: 0,     y: 0, z: 0 },
        { x: 0.02,  y: 0, z: 0 },
        { x: 0.004, y: 0, z: 0 },
        { x: 0.018, y: 0, z: 0 },
        { x: 0.004, y: 0, z: 0 },
        { x: 0,     y: 0, z: 0 },
      ],
    },
  },

  // Shake (no): head turns LEFT then RIGHT then back, twice, then return.
  // worldSpaceAxes so the rotation is always around the world's
  // vertical Y-axis (turning head side-to-side).
  shake: {
    worldSpaceAxes: true,
    times: [0, 0.20, 0.40, 0.60, 0.80, 1.05],
    bones: {
      head: [
        { x: 0, y: 0,     z: 0 },
        { x: 0, y: 0.20,  z: 0 },
        { x: 0, y: -0.20, z: 0 },
        { x: 0, y: 0.16,  z: 0 },
        { x: 0, y: -0.10, z: 0 },
        { x: 0, y: 0,     z: 0 },
      ],
      neck: [
        { x: 0, y: 0,     z: 0 },
        { x: 0, y: 0.04,  z: 0 },
        { x: 0, y: -0.04, z: 0 },
        { x: 0, y: 0.03,  z: 0 },
        { x: 0, y: -0.02, z: 0 },
        { x: 0, y: 0,     z: 0 },
      ],
    },
  },

  // Bilateral shoulder lift: a real shrug.
  //
  // Slice 15 (May 15 2026): switched to worldSpaceAxes for rig-agnostic
  // axis interpretation.
  //
  // Slice 17 (May 16 2026): empirically the avatars in our library face
  // +Z (TOWARD the camera, not away), which puts the right hand at -X
  // and the left hand at +X. With +Z rotation by +0.32 rad applied to
  // the right clavicle (whose tip is at -X), the geometry says the
  // shoulder Y-position decreases (clavicle pivots in the X-Y plane
  // and a -X point moves DOWN on a +Z rotation when X<0). The user's
  // regression report ("shoulders pushed DOWN") matched this exactly,
  // so we sign-flipped the Z values:
  //   • rightShoulder: NEGATIVE Z → lifts UP for -X-side bones
  //   • leftShoulder:  POSITIVE Z → lifts UP for +X-side bones
  //
  // We also reduced the magnitude (0.32 → 0.08 peak) because the
  // user reported the shrug was "too pronounced" — a real human
  // shrug is ~10–15° at the shoulder, not the ~18° Slice 15 was
  // hitting. The new peak is ~4.5° which reads as a subtle "not
  // sure" rather than an "I-give-up" gesture.
  shrug: {
    worldSpaceAxes: true,
    times: [0, 0.25, 0.5, 0.85, 1.1],
    bones: {
      rightShoulder: [
        { x: 0, y: 0, z: 0 },
        { x: 0, y: 0, z: -0.065 },
        { x: 0, y: 0, z: -0.08 },
        { x: 0, y: 0, z: -0.045 },
        { x: 0, y: 0, z: 0 },
      ],
      leftShoulder: [
        { x: 0, y: 0, z: 0 },
        { x: 0, y: 0, z: 0.065 },
        { x: 0, y: 0, z: 0.08 },
        { x: 0, y: 0, z: 0.045 },
        { x: 0, y: 0, z: 0 },
      ],
    },
  },

  // Lean forward from the spine + a hint of upper-chest follow-through.
  // World-space axes: `x` is the rotation around the world's x-axis
  // (left-right), so a positive value tilts the upper body toward the
  // camera regardless of how each rig's spine bone was authored. Without
  // this flag, rigs whose spine local-X points sideways in world space
  // (Cara, Jason, Joel, several Xandra-derived females) lean LEFT-RIGHT
  // instead of forward/back. Sam's spine happens to align with the
  // Mixamo convention so the OLD bone-local path produced the correct
  // tilt for him — that's why he was the only model the operator
  // reported as "accurate" before this fix.
  'lean-forward': {
    worldSpaceAxes: true,
    times: [0, 0.4, 0.7, 1.0, 1.4],
    bones: {
      spine: [
        { x: 0, y: 0, z: 0 },
        { x: 0.08, y: 0, z: 0 },
        { x: 0.10, y: 0, z: 0 },
        { x: 0.06, y: 0, z: 0 },
        { x: 0, y: 0, z: 0 },
      ],
      chest: [
        { x: 0, y: 0, z: 0 },
        { x: 0.03, y: 0, z: 0 },
        { x: 0.04, y: 0, z: 0 },
        { x: 0.025, y: 0, z: 0 },
        { x: 0, y: 0, z: 0 },
      ],
      // Neck follows the torso so the head stays on the chain instead of
      // floating from the face-only lean blendshape headRotation offsets.
      neck: [
        { x: 0, y: 0, z: 0 },
        { x: 0.025, y: 0, z: 0 },
        { x: 0.032, y: 0, z: 0 },
        { x: 0.018, y: 0, z: 0 },
        { x: 0, y: 0, z: 0 },
      ],
    },
  },

  // Lean back: spine pulls back, slight chest counter-rotation.
  // Same worldSpaceAxes rationale as lean-forward — the negative `x`
  // value here means "rotate AWAY from camera around world-X"
  // regardless of bone-local orientation.
  'lean-back': {
    worldSpaceAxes: true,
    times: [0, 0.4, 0.7, 1.0, 1.4],
    bones: {
      spine: [
        { x: 0, y: 0, z: 0 },
        { x: -0.06, y: 0, z: 0 },
        { x: -0.08, y: 0, z: 0 },
        { x: -0.05, y: 0, z: 0 },
        { x: 0, y: 0, z: 0 },
      ],
      chest: [
        { x: 0, y: 0, z: 0 },
        { x: -0.03, y: 0, z: 0 },
        { x: -0.04, y: 0, z: 0 },
        { x: -0.02, y: 0, z: 0 },
        { x: 0, y: 0, z: 0 },
      ],
      neck: [
        { x: 0, y: 0, z: 0 },
        { x: -0.02, y: 0, z: 0 },
        { x: -0.028, y: 0, z: 0 },
        { x: -0.015, y: 0, z: 0 },
        { x: 0, y: 0, z: 0 },
      ],
    },
  },
};

/**
 * Bone names for the procedural wave animation (Mixamo rig convention).
 * @deprecated Use {@link GESTURE_BONE_KEYFRAMES} + {@link BONE_CANDIDATES}.
 * Kept for back-compat with consumers that haven't been re-vendored yet.
 */
export const WAVE_BONE_NAMES = {
  shoulder: 'RightShoulder',
  upperArm: 'RightArm',
  foreArm: 'RightForeArm',
  hand: 'RightHand',
} as const;

/**
 * Procedural wave keyframe data (legacy single-arm shape).
 * @deprecated Use {@link GESTURE_BONE_KEYFRAMES}.wave.
 */
export const WAVE_BONE_KEYFRAMES = {
  times: GESTURE_BONE_KEYFRAMES.wave!.times,
  shoulder: GESTURE_BONE_KEYFRAMES.wave!.bones.rightShoulder!,
  upperArm: GESTURE_BONE_KEYFRAMES.wave!.bones.rightUpperArm!,
  foreArm:  GESTURE_BONE_KEYFRAMES.wave!.bones.rightForeArm!,
  hand:     GESTURE_BONE_KEYFRAMES.wave!.bones.rightHand!,
};

/** Whether a gesture type carries skeletal (bone) keyframe data. */
export function isSkeletalGesture(type: GestureType): boolean {
  return type in GESTURE_BONE_KEYFRAMES;
}

/**
 * Resolve a logical bone role to an actual bone name on the rig, by walking
 * the candidate list. Returns `null` if no candidate exists on this skeleton.
 *
 * If `getActualBoneName` is supplied, the resolver also tries a
 * case-insensitive fallback over the candidate list — this catches rigs
 * authored with non-standard casing (e.g. `RIGHTSHOULDER`, `right_Shoulder`,
 * `Spine_03`) that still semantically match a known role. The exact-match
 * pass is tried first so well-formed Mixamo/UE5/MetaHuman rigs keep their
 * canonical names in debug logs.
 */
export function resolveBoneNameForRole(
  role: BoneRole,
  hasBone: (name: string) => boolean,
  getActualBoneName?: (lowerName: string) => string | null,
): string | null {
  for (const candidate of BONE_CANDIDATES[role]) {
    if (hasBone(candidate)) return candidate;
  }
  if (getActualBoneName) {
    for (const candidate of BONE_CANDIDATES[role]) {
      const actual = getActualBoneName(candidate.toLowerCase());
      if (actual) return actual;
    }
  }
  return null;
}

// Gesture animation data
export const GESTURE_DATA: Record<GestureType, GestureFrame[]> = {
  'nod': nodData as unknown as GestureFrame[],
  'shake': shakeData as unknown as GestureFrame[],
  'shrug': shrugData as unknown as GestureFrame[],
  'lean-forward': generateLeanForwardData(),
  'lean-back': generateLeanBackData(),
  'wave': generateWaveFaceData()
};

/**
 * Detect which gesture (if any) should be triggered based on text content
 */
export function detectGestureFromText(text: string): GestureType | null {
  const lowerText = text.toLowerCase();

  // Check each gesture type in priority order (wave first for greeting/farewell detection)
  const gestureOrder: GestureType[] = ['wave', 'nod', 'shake', 'shrug', 'lean-forward', 'lean-back'];

  for (const gestureType of gestureOrder) {
    const triggers = GESTURE_TRIGGERS[gestureType];
    for (const trigger of triggers) {
      if (lowerText.includes(trigger)) {
        console.log(`🎭 Gesture detected: ${gestureType} (trigger: "${trigger}")`);
        return gestureType;
      }
    }
  }

  // Emoji detection (raw unicode, not lowercased)
  if (text.includes('\u{1F44B}')) return 'wave';
  if (text.includes('\u{1F44D}')) return 'nod';

  return null;
}

/**
 * Get animation data for a gesture type
 */
export function getGestureData(type: GestureType): GestureFrame[] {
  return GESTURE_DATA[type] || [];
}

/**
 * Get gesture duration in seconds
 */
export function getGestureDuration(type: GestureType): number {
  const data = getGestureData(type);
  if (data.length === 0) return 0;
  return data[data.length - 1].time;
}

/**
 * Scale gesture intensity
 */
export function scaleGestureIntensity(
  frames: GestureFrame[],
  intensity: number
): GestureFrame[] {
  return frames.map(frame => ({
    ...frame,
    blendshapes: Object.fromEntries(
      Object.entries(frame.blendshapes).map(([key, value]) => [key, value * intensity])
    ),
    headPosition: frame.headPosition ? {
      x: frame.headPosition.x * intensity,
      y: frame.headPosition.y * intensity,
      z: frame.headPosition.z * intensity
    } : undefined,
    headRotation: frame.headRotation ? {
      x: frame.headRotation.x * intensity,
      y: frame.headRotation.y * intensity,
      z: frame.headRotation.z * intensity
    } : undefined
  }));
}

/**
 * Gesture controller for managing gesture playback
 */
export class GestureController {
  private isPlaying = false;
  private currentGesture: GestureType | null = null;
  private animationFrameId: number | null = null;
  private startTime = 0;
  private onFrameCallback: ((frame: GestureFrame, progress: number) => void) | null = null;
  private onCompleteCallback: (() => void) | null = null;
  /** Last interpolated frame — re-applied after mixer.update on UE5 rigs. */
  private latestFrame: GestureFrame | null = null;

  getLatestFrame(): GestureFrame | null {
    return this.latestFrame;
  }

  /**
   * Play a gesture animation
   */
  play(
    config: GestureConfig,
    onFrame: (frame: GestureFrame, progress: number) => void,
    onComplete?: () => void
  ): void {
    // Stop any current gesture
    this.stop();

    const frames = getGestureData(config.type);
    if (frames.length === 0) {
      console.warn(`No animation data for gesture: ${config.type}`);
      return;
    }

    const intensity = config.intensity ?? 1.0;
    const scaledFrames = intensity !== 1.0 ? scaleGestureIntensity(frames, intensity) : frames;
    const duration = config.duration ?? getGestureDuration(config.type);

    this.isPlaying = true;
    this.currentGesture = config.type;
    this.startTime = performance.now();
    this.onFrameCallback = onFrame;
    this.onCompleteCallback = onComplete ?? null;

    console.log(`🎭 Playing gesture: ${config.type} (duration: ${duration}s, intensity: ${intensity})`);

    const animate = () => {
      if (!this.isPlaying) return;

      const elapsed = (performance.now() - this.startTime) / 1000;
      const progress = Math.min(elapsed / duration, 1);

      // Find the current frame based on progress
      const currentTime = progress * duration;
      let frameIndex = 0;
      for (let i = 0; i < scaledFrames.length - 1; i++) {
        if (scaledFrames[i + 1].time > currentTime) {
          frameIndex = i;
          break;
        }
        frameIndex = i;
      }

      // Interpolate between frames
      const frame1 = scaledFrames[frameIndex];
      const frame2 = scaledFrames[Math.min(frameIndex + 1, scaledFrames.length - 1)];
      const frameDuration = frame2.time - frame1.time;
      const frameProgress = frameDuration > 0 ? (currentTime - frame1.time) / frameDuration : 0;

      const interpolatedFrame = this.interpolateFrames(frame1, frame2, frameProgress);
      this.latestFrame = interpolatedFrame;

      if (this.onFrameCallback) {
        this.onFrameCallback(interpolatedFrame, progress);
      }

      if (progress >= 1) {
        this.stop();
        if (this.onCompleteCallback) {
          this.onCompleteCallback();
        }
      } else {
        this.animationFrameId = requestAnimationFrame(animate);
      }
    };

    this.animationFrameId = requestAnimationFrame(animate);
  }

  /**
   * Interpolate between two frames
   */
  private interpolateFrames(
    frame1: GestureFrame,
    frame2: GestureFrame,
    t: number
  ): GestureFrame {
    const blendshapes: Record<string, number> = {};
    const allKeys = Array.from(new Set([
      ...Object.keys(frame1.blendshapes),
      ...Object.keys(frame2.blendshapes)
    ]));

    allKeys.forEach(key => {
      const v1 = frame1.blendshapes[key] || 0;
      const v2 = frame2.blendshapes[key] || 0;
      blendshapes[key] = v1 + (v2 - v1) * t;
    });

    return {
      blendshapes,
      headPosition: this.interpolateVector(frame1.headPosition, frame2.headPosition, t),
      headRotation: this.interpolateVector(frame1.headRotation, frame2.headRotation, t),
      time: frame1.time + (frame2.time - frame1.time) * t
    };
  }

  private interpolateVector(
    v1: { x: number; y: number; z: number } | undefined,
    v2: { x: number; y: number; z: number } | undefined,
    t: number
  ): { x: number; y: number; z: number } | undefined {
    if (!v1 && !v2) return undefined;
    const a = v1 || { x: 0, y: 0, z: 0 };
    const b = v2 || { x: 0, y: 0, z: 0 };
    return {
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t,
      z: a.z + (b.z - a.z) * t
    };
  }

  /**
   * Stop current gesture
   */
  stop(): void {
    this.isPlaying = false;
    this.currentGesture = null;
    this.latestFrame = null;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Check if a gesture is currently playing
   */
  isGesturePlaying(): boolean {
    return this.isPlaying;
  }

  /**
   * Get current gesture type
   */
  getCurrentGesture(): GestureType | null {
    return this.currentGesture;
  }
}

// Singleton instance
let gestureControllerInstance: GestureController | null = null;

export function getGestureController(): GestureController {
  if (!gestureControllerInstance) {
    gestureControllerInstance = new GestureController();
  }
  return gestureControllerInstance;
}

export default {
  detectGestureFromText,
  getGestureData,
  getGestureDuration,
  scaleGestureIntensity,
  GestureController,
  getGestureController,
  isSkeletalGesture,
  resolveBoneNameForRole,
  GESTURE_TRIGGERS,
  GESTURE_DATA,
  GESTURE_BONE_KEYFRAMES,
  BONE_CANDIDATES,
  WAVE_BONE_NAMES,
  WAVE_BONE_KEYFRAMES,
};
